// Updated notification preference utility functions
export const PREF_KEY = "notification_prompt_preference";
const COOKIE_PREF_KEY = "notification_permission";
const COOKIE_DOMAIN_SUFFIX = process.env.ICM_URL?.substring(process.env.ICM_URL?.indexOf(".") + 1); // Update to your domain

// Utility: Set cookie with domain to share across subdomains
function setCookie(name: string, value: string, days: number = 3) {
    if (typeof document === "undefined") return;

    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;

    // Set cookie with domain to make it accessible across all subdomains
    document.cookie = `${name}=${value}; ${expires}; path=/; domain=${COOKIE_DOMAIN_SUFFIX}; SameSite=Lax`;
}

// Utility: Get cookie value
function getCookie(name: string): string | null {
    if (typeof document === "undefined") return null;

    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(";");

    for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.startsWith(nameEQ)) {
            return cookie.substring(nameEQ.length);
        }
    }
    return null;
}

// Utility: Delete cookie
function deleteCookie(name: string) {
    if (typeof document === "undefined") return;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${COOKIE_DOMAIN_SUFFIX};`;
}

export function getUserNotificationPreference(): "granted" | "denied" | null {
    const notificationSupported = "Notification" in window;
    if (typeof window === "undefined" || !notificationSupported) return null;

    const actualPermission = Notification.permission;
    const storedPref = localStorage.getItem(PREF_KEY) as "granted" | "denied" | null;
    const cookiePref = getCookie(COOKIE_PREF_KEY) as "granted" | "denied" | null;

    // Priority: Browser permission > Cookie > LocalStorage > null

    // If browser permission is "default" but we have a stored preference of "denied",
    // it means user previously declined our custom prompt (not browser prompt)
    if (actualPermission === "default" && (storedPref === "denied" || cookiePref === "denied")) {
        return "denied"; // Don't clear storage, respect user's choice
    }

    // If browser permission changed from stored preference, sync cookies and localStorage
    if (actualPermission !== "default" && storedPref !== actualPermission) {
        localStorage.setItem(PREF_KEY, actualPermission);
        setCookie(COOKIE_PREF_KEY, actualPermission);
        return actualPermission;
    }

    // Check cookie preference (cross-subdomain)
    if (actualPermission === "default" && cookiePref) {
        // Sync cookie back to localStorage
        localStorage.setItem(PREF_KEY, cookiePref);
        return cookiePref;
    }

    // If browser permission is "default" and no stored preference, return null (show prompt)
    if (actualPermission === "default" && !storedPref && !cookiePref) {
        return null;
    }

    return storedPref || cookiePref;
}

/* export function getUserNotificationPreference(): "granted" | "denied" | null {
    const notificationSupported = "Notification" in window;
    if (typeof window === "undefined" || !notificationSupported) return null;

    const actualPermission = Notification.permission;
    const storedPref = localStorage.getItem(PREF_KEY) as "granted" | "denied" | null;

    // If browser permission is "default" but we have a stored preference of "denied",
    // it means user previously declined our custom prompt (not browser prompt)
    if (actualPermission === "default" && storedPref === "denied") {
        return "denied"; // Don't clear localStorage, respect user's choice
    }

    // If browser permission changed from stored preference, sync them
    if (actualPermission !== "default" && storedPref !== actualPermission) {
        localStorage.setItem(PREF_KEY, actualPermission);
        return actualPermission;
    }

    // If browser permission is "default" and no stored preference, return null (show prompt)
    if (actualPermission === "default" && !storedPref) {
        return null;
    }

    return storedPref;
} */

export function setUserNotificationPreference(pref: "granted" | "denied" | "default") {
    if (typeof window === "undefined") return;
    localStorage.setItem(PREF_KEY, pref);
    setCookie(COOKIE_PREF_KEY, pref);
}

// New utility: Clear notification preferences across subdomains
export function clearNotificationPreference() {
    if (typeof window === "undefined") return;

    localStorage.removeItem(PREF_KEY);
    deleteCookie(COOKIE_PREF_KEY);
}

// New utility: Sync permission state across tabs/subdomains
export function syncNotificationPreference() {
    if (typeof window === "undefined" || !("Notification" in window)) return;

    const actualPermission = Notification.permission;

    if (actualPermission !== "default") {
        setCookie(COOKIE_PREF_KEY, actualPermission);
        localStorage.setItem(PREF_KEY, actualPermission);
    }
}
